import React, { useEffect } from 'react';

function EmailLink({ text }) {
  const parts = text.split('contact@gigglygains.com');
  if (parts.length === 1) return text;
  return parts.map((part, i, arr) => (
    <React.Fragment key={i}>
      {part}
      {i < arr.length - 1 && (
        <a href="mailto:contact@gigglygains.com" className="text-[#F97D6A] hover:underline">contact@gigglygains.com</a>
      )}
    </React.Fragment>
  ));
}

function P({ children }) {
  return <p className="text-sm sm:text-[15px] text-[#4B5563] leading-[1.8]">{children}</p>;
}

function Bullets({ items }) {
  return (
    <ul className="mt-3 space-y-1.5 ml-1">
      {items.map((b, i) => (
        <li key={i} className="flex gap-2.5 text-sm sm:text-[15px] text-[#4B5563] leading-[1.8]">
          <span className="mt-[10px] h-1.5 w-1.5 rounded-full bg-[#D1D5DB] shrink-0" />
          <span><EmailLink text={b} /></span>
        </li>
      ))}
    </ul>
  );
}

function Section({ num, title, children }) {
  return (
    <section data-testid={`safety-section-${num}`}>
      <h2 className="text-base sm:text-lg font-bold text-[#1F2937] font-['Outfit'] mb-3">
        {num}. {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Sub({ title }) {
  return <h3 className="text-sm sm:text-base font-semibold text-[#1F2937] font-['Outfit'] mt-5 mb-2">{title}</h3>;
}

export default function StudentSafetyPage() {
  useEffect(() => {
    document.title = 'Student Safety & Experience Guidelines | CareersPOV';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Learn about CareersPOV student safety guidelines, mentor responsibilities, parental permissions, online career experiences, workplace visits, child protection and safeguarding.');
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="py-8 sm:py-12" data-testid="student-safety-page">
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
        <header className="mb-10 border-b border-gray-200 pb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1F2937] font-['Outfit'] mb-2">
            Student Safety & Experience Guidelines
          </h1>
          <p className="text-sm text-[#6B7280] mb-1">
            CareersPOV &nbsp;|&nbsp; Operated by Giggly Gains
          </p>
          <p className="text-xs text-[#9CA3AF]">
            Effective Date: 9 October 2026 &nbsp;&middot;&nbsp; Last Updated: 9 October 2026
          </p>
        </header>

        <div className="space-y-8">

{/* ── 1 ── */}
<Section num={1} title="Purpose and Scope">
  <P>CareersPOV is a career exploration and experiential learning platform operated by Giggly Gains, a partnership firm based in Pune, Maharashtra, India.</P>
  <P>CareersPOV helps school students explore careers, understand professional environments and participate in educational experiences facilitated through their school communities.</P>
  <P>The safety, dignity, privacy and well-being of students are fundamental to every CareersPOV experience.</P>
  <P>These Student Safety & Experience Guidelines establish the standards and responsibilities applicable to participating schools, students, parents, legal guardians, mentors, host organizations and other individuals involved in CareersPOV activities.</P>
  <P>These Guidelines apply to:</P>
  <Bullets items={[
    'Online career interactions and mentoring sessions.',
    'Career talks and workshops.',
    'Workplace visits and job shadowing.',
    'Internships and extended professional experiences.',
    'Communications associated with approved experiences.',
    'Other career exploration activities facilitated through CareersPOV.',
  ]} />
  <div className="mt-3 space-y-3">
    <P>These Guidelines supplement, rather than replace, applicable school safeguarding policies, child-protection procedures and legal requirements.</P>
    <P>Where applicable law or school policy imposes stricter safeguards, those safeguards must be followed.</P>
  </div>
</Section>

{/* ── 2 ── */}
<Section num={2} title="Roles and Responsibilities">
  <Sub title="Participating Schools" />
  <P>Participating schools are responsible for administering their CareersPOV communities and establishing appropriate safeguarding arrangements for school-organized activities.</P>
  <P>Their responsibilities include:</P>
  <Bullets items={[
    'Approving student participation.',
    'Assessing and approving mentor suitability.',
    'Establishing appropriate supervision arrangements.',
    'Communicating experience requirements to parents.',
    'Obtaining necessary parental permissions.',
    'Reviewing safety concerns and reported incidents.',
    'Coordinating with host organizations.',
    'Maintaining appropriate safeguarding procedures.',
    'Complying with applicable child-protection and reporting obligations.',
  ]} />
  <div className="mt-3 space-y-3">
    <P>Each school must identify an authorized CareersPOV coordinator and the appropriate school authority responsible for student safeguarding.</P>
    <P>The CareersPOV coordinator may manage routine administration and receive concerns. Serious safeguarding matters must be escalated promptly to the school's designated safeguarding authority and, where required, competent external authorities.</P>
  </div>

  <Sub title="Parents and Legal Guardians" />
  <P>Parents and legal guardians are responsible for:</P>
  <Bullets items={[
    'Reviewing experience details before approving participation.',
    'Providing accurate information and required permissions.',
    'Understanding relevant supervision and safety arrangements.',
    'Explaining appropriate safety boundaries to their children.',
    'Reporting concerns to the school.',
    'Supporting children who disclose uncomfortable or inappropriate experiences.',
  ]} />
  <div className="mt-3"><P>Parental authorization does not replace the school's responsibility to establish appropriate safeguarding arrangements.</P></div>

  <Sub title="Mentors and Host Professionals" />
  <P>Mentors and host professionals must:</P>
  <Bullets items={[
    'Provide accurate professional information.',
    'Follow school safeguarding requirements.',
    'Conduct experiences professionally and respectfully.',
    'Maintain appropriate boundaries with students.',
    'Protect student information.',
    'Provide age-appropriate educational experiences.',
    'Follow approved supervision arrangements.',
    'Report safety concerns promptly.',
    'Comply with applicable legal requirements.',
  ]} />

  <Sub title="Students" />
  <P>Students are expected to:</P>
  <Bullets items={[
    'Treat others respectfully.',
    'Follow appropriate school and experience instructions.',
    'Respect workplace safety rules.',
    'Protect their own and others\u2019 privacy.',
    'Avoid sharing unnecessary personal information.',
    'Report conduct that makes them uncomfortable or unsafe.',
  ]} />
  <div className="mt-3"><P>Students must not be pressured to continue an interaction or activity that makes them feel unsafe.</P></div>

  <Sub title="Giggly Gains" />
  <P>Giggly Gains operates the CareersPOV technology platform and is responsible for taking reasonable measures within its control to address platform-related safety risks.</P>
  <P>Giggly Gains may restrict platform access, preserve appropriate records, assist participating schools and cooperate with competent authorities where necessary.</P>
</Section>

{/* ── 3 ── */}
<Section num={3} title="Mentor Approval and Suitability">
  <P>Mentors must be approved by the participating school before conducting experiences with students through its CareersPOV community.</P>
  <P>Participating schools must assess mentor suitability before authorizing student interactions.</P>
  <P>The assessment must be appropriate to:</P>
  <Bullets items={[
    'The student\u2019s age.',
    'The nature of the experience.',
    'The level and duration of contact.',
    'Whether the activity is an online session or a workplace visit.',
    'The supervision arrangements.',
    'Applicable school policies and legal requirements.',
  ]} />
  <div className="mt-3 space-y-3">
    <P>Schools are responsible for determining and completing any identity verification, professional verification, reference checks or background checks required by applicable law or their safeguarding policies.</P>
    <P>Mentors must provide accurate information and must not misrepresent their identity, qualifications, professional experience or affiliation.</P>
    <P>Approval through CareersPOV confirms authorization by the participating school.</P>
    <P>It does not, by itself, certify that a mentor has passed an independent criminal background check or professional verification conducted by Giggly Gains.</P>
    <P>A school may withdraw mentor approval where appropriate.</P>
    <P>Giggly Gains may independently restrict platform access where credible platform-related safety concerns arise.</P>
  </div>
</Section>

{/* ── 4 ── */}
<Section num={4} title="Parental Authorization">
  <P>Students must not participate in experiences requiring parental authorization without the necessary approval.</P>
  <P>Before confirming participation, parents should receive relevant information, including:</P>
  <Bullets items={[
    'Nature and educational purpose of the experience.',
    'Mentor or host organization.',
    'Date, time and expected duration.',
    'Online or physical location.',
    'Applicable supervision arrangements.',
    'Any special requirements or foreseeable risks.',
  ]} />
  <div className="mt-3"><P>Additional authorization may be required for:</P></div>
  <Bullets items={[
    'Workplace visits.',
    'Job shadowing.',
    'Internships.',
    'Travel or transportation.',
    'Photography or recording.',
    'Activities involving additional safety considerations.',
  ]} />
  <div className="mt-3 space-y-3">
    <P>Approval to participate in an experience is separate from consent to process personal information.</P>
    <P>Schools must ensure that required permissions and safeguarding arrangements are established before an experience begins.</P>
  </div>
</Section>

{/* ── 5 ── */}
<Section num={5} title="Online Career Experiences">
  <P>Online career experiences must be conducted through arrangements approved by the participating school.</P>
  <P>All interactions must remain professional, respectful, educational and age-appropriate.</P>
  <P>The following standards apply:</P>
  <Bullets items={[
    'Sessions must focus on approved educational or career-related topics.',
    'Mentors must not request unnecessary personal information.',
    'Students must not be pressured to disclose sensitive information.',
    'Harassment, discrimination and inappropriate conduct are prohibited.',
    'Meeting links must not be shared with unauthorized individuals.',
    'Recording or distributing sessions requires appropriate authorization.',
    'Unauthorized private communication between mentors and students is prohibited.',
  ]} />

  <Sub title="Individual Online Sessions" />
  <P>Individual interactions between students under 18 and adult mentors must take place under safeguarding arrangements approved by the participating school.</P>
  <P>Such arrangements must provide appropriate adult oversight.</P>
  <P>Depending on the student's age and the nature of the experience, this may involve a parent, school representative or another authorized adult.</P>
  <P>Unsupervised private meetings outside approved safeguarding arrangements are prohibited.</P>
  <P>Schools must determine and communicate suitable supervision arrangements before approving individual experiences.</P>
</Section>

{/* ── 6 ── */}
<Section num={6} title="Communication Boundaries">
  <P>All communication between mentors and students must remain professional and related to an approved career experience.</P>
  <P>Mentors must not:</P>
  <Bullets items={[
    'Initiate unauthorized private communication with students.',
    'Request personal phone numbers or private social media accounts.',
    'Send inappropriate or personal messages.',
    'Arrange meetings outside approved activities.',
    'Request unnecessary photographs or personal information.',
    'Offer gifts or incentives in exchange for private communication.',
    'Seek personal favours or establish inappropriate relationships.',
    'Engage in romantic, sexual, exploitative or otherwise inappropriate conduct.',
    'Use student information for commercial solicitation.',
  ]} />
  <div className="mt-3 space-y-3">
    <P>Follow-up communication should occur through the parent, school coordinator or another school-approved channel.</P>
    <P>Students should report unauthorized contact to a parent, teacher, school coordinator or another trusted adult.</P>
  </div>
</Section>

{/* ── 7 ── */}
<Section num={7} title="Workplace Visits and Job Shadowing">
  <P>Workplace visits require additional planning and safeguarding.</P>
  <P>Before approving an experience, the participating school must establish appropriate arrangements covering:</P>
  <Bullets items={[
    'Workplace suitability.',
    'Age-appropriate activities.',
    'Foreseeable workplace hazards.',
    'Student supervision.',
    'Transportation responsibilities.',
    'Student arrival, handover and collection.',
    'Emergency contact arrangements.',
    'Accessibility and individual participation needs.',
    'Applicable permissions and insurance requirements.',
  ]} />
  <div className="mt-3 space-y-3">
    <P>Host organizations must provide appropriate supervision and explain relevant workplace safety rules.</P>
    <P>Students must not be assigned hazardous, prohibited or age-inappropriate activities.</P>
    <P>Students must not be left alone in circumstances that create an unreasonable safeguarding risk.</P>
  </div>

  <Sub title="Transportation" />
  <P>Transportation responsibilities must be established and communicated before the experience.</P>
  <P>Students must not be transported privately by mentors or host professionals unless the arrangement has been expressly authorized through the school's safeguarding procedures and by the parent or legal guardian.</P>
  <P>Parents and schools must receive appropriate location and contact information before participation.</P>
</Section>

{/* ── 8 ── */}
<Section num={8} title="Internships and Extended Experiences">
  <P>Internships and extended professional experiences require additional oversight.</P>
  <P>Before participation, the school and host organization must establish appropriate arrangements covering:</P>
  <Bullets items={[
    'Educational objectives.',
    'Duration and schedule.',
    'Assigned supervisor.',
    'Permitted activities.',
    'Workplace safety requirements.',
    'Parental authorization.',
    'Attendance and reporting.',
    'Emergency arrangements.',
    'Applicable age and legal restrictions.',
  ]} />
  <div className="mt-3 space-y-3">
    <P>Students must not perform work prohibited for their age or circumstances under applicable law.</P>
    <P>Students must not be used as a substitute for workers or assigned exploitative activities.</P>
    <P>Participation through CareersPOV does not automatically establish an employment relationship.</P>
    <P>Any employment, compensation, insurance or other legal arrangements must be addressed separately where applicable.</P>
  </div>
</Section>

{/* ── 9 ── */}
<Section num={9} title="Photography, Video and Recording">
  <P>Student privacy must be respected during all experiences.</P>
  <P>Consent to participate in a CareersPOV experience does not automatically authorize photography, recording, publication or commercial use of a student's name, image, voice or other identifying information.</P>
  <P>Separate, specific authorization must be obtained where required.</P>
  <P>This applies to:</P>
  <Bullets items={[
    'Photographs.',
    'Video recordings.',
    'Audio recordings.',
    'Screenshots.',
    'Social media posts.',
    'Testimonials identifying students.',
    'Promotional materials.',
  ]} />
  <div className="mt-3 space-y-3">
    <P>Students must not be pressured to participate in photography, recordings, testimonials or promotional activities.</P>
    <P>Recordings and images must be stored, accessed, retained and deleted in accordance with applicable privacy requirements.</P>
    <P>Unauthorized recording or publication is prohibited.</P>
  </div>
</Section>

{/* ── 10 ── */}
<Section num={10} title="Student Privacy and Confidentiality">
  <P>Personal information shared through CareersPOV must be used only for authorized educational and administrative purposes.</P>
  <P>Mentors and host organizations must not:</P>
  <Bullets items={[
    'Collect unnecessary student information.',
    'Copy or distribute student records without authorization.',
    'Use student information for unrelated marketing or recruitment.',
    'Retain student information beyond an authorized purpose.',
    'Disclose student information to unauthorized individuals.',
  ]} />
  <div className="mt-3 space-y-3">
    <P>Schools, mentors and host organizations must comply with applicable privacy requirements and the CareersPOV Privacy Policy.</P>
    <P>Student-related privacy requests should ordinarily be directed to the participating school's designated CareersPOV coordinator.</P>
    <P>Platform-specific privacy and security concerns may be reported to Giggly Gains.</P>
  </div>
</Section>

{/* ── 11 ── */}
<Section num={11} title="Respectful Conduct and Non-Discrimination">
  <P>All participants must be treated with dignity and respect.</P>
  <P>CareersPOV experiences must not involve:</P>
  <Bullets items={[
    'Bullying or intimidation.',
    'Harassment.',
    'Sexual harassment or exploitation.',
    'Discriminatory conduct.',
    'Humiliation or degrading treatment.',
    'Inappropriate physical contact.',
    'Threats or coercion.',
    'Financial exploitation.',
    'Unauthorized commercial solicitation.',
  ]} />
  <div className="mt-3 space-y-3">
    <P>Mentors and host organizations must not request money, gifts, personal favours or unpaid work unrelated to the approved educational experience.</P>
    <P>Any fee-based experience must be separately disclosed and approved through appropriate school and parental arrangements.</P>
    <P>Students must not be pressured to disclose personal beliefs, family circumstances or sensitive information unrelated to the educational purpose.</P>
  </div>
</Section>

{/* ── 12 ── */}
<Section num={12} title="Reporting Student Safety Concerns">
  <P>Students, parents, mentors and school representatives should promptly report suspected safety concerns.</P>
  <P>These may include:</P>
  <Bullets items={[
    'Inappropriate mentor communication.',
    'Harassment or bullying.',
    'Unsafe workplace conditions.',
    'Unauthorized photography or recording.',
    'Requests for private meetings.',
    'Misuse of student information.',
    'Suspected abuse or exploitation.',
    'Other conduct creating a safeguarding concern.',
  ]} />
  <div className="mt-3 space-y-3">
    <P>The participating school's designated CareersPOV coordinator is the primary contact for routine student safety concerns.</P>
    <P>Serious safeguarding concerns must be escalated promptly to the school's designated safeguarding authority and, where required, competent external authorities.</P>
    <P>Where a concern involves the coordinator or another school representative, students and parents may report directly to the principal, designated safeguarding authority or competent authorities.</P>
  </div>

  <Sub title="Protection of Individuals Reporting Concerns" />
  <P>Reports must be handled sensitively.</P>
  <P>Information should be shared only with persons who reasonably require it for safeguarding, investigation or legal compliance.</P>
  <P>Retaliation against a student or another person raising a concern in good faith is prohibited.</P>
  <P>Students must not be required to confront an alleged offender or continue participating in an activity while a credible safety concern is being assessed.</P>
  <P>Confidentiality cannot be guaranteed where disclosure is necessary to protect a child or comply with law.</P>
</Section>

{/* ── 13 ── */}
<Section num={13} title="Mandatory Reporting Under POCSO">
  <P>The Protection of Children from Sexual Offences Act, 2012 (POCSO) establishes legal obligations concerning the reporting of certain offences involving children.</P>
  <P>Where a person has knowledge that an offence covered by POCSO has been committed, or apprehends that such an offence is likely to be committed, the matter must be reported to the Special Juvenile Police Unit or local police in accordance with Section 19 of the Act.</P>
  <P>Reporting a matter to a school coordinator, principal, mentor or CareersPOV does not replace mandatory reporting to the appropriate authorities.</P>
  <P>A legally required report must not be delayed while awaiting an internal investigation or administrative approval.</P>
  <P>Schools, mentors, host organizations and other participants must comply with applicable mandatory reporting obligations.</P>
  <P>Giggly Gains will comply with reporting obligations applicable to information or incidents brought to its attention.</P>
</Section>

{/* ── 14 ── */}
<Section num={14} title="Immediate Danger and Emergencies">
  <P>If a student is in immediate danger, participants must prioritize the student's safety and contact appropriate emergency services or authorities.</P>
  <P>In India:</P>
  <div className="mt-2 flex flex-col gap-1">
    <P><span className="font-semibold text-[#1F2937]">Emergency Response:</span> 112</P>
    <P><span className="font-semibold text-[#1F2937]">Child Helpline:</span> 1098</P>
  </div>
  <div className="mt-3 space-y-3">
    <P>The school and parent or legal guardian should be informed as appropriate, taking account of the child's safety and applicable legal requirements.</P>
    <P>CareersPOV is not an emergency response service.</P>
  </div>

  {/* Student-friendly safety message with subtle emphasis */}
  <div className="mt-5 rounded-xl border border-[#F97D6A]/20 bg-[#FFF8F6] p-5 sm:p-6">
    <h3 className="text-sm sm:text-base font-bold text-[#1F2937] font-['Outfit'] mb-3">Important Information for Students</h3>
    <div className="space-y-3">
      <P>If something during a CareersPOV experience makes you uncomfortable, unsafe or worried, you can stop participating and tell a parent, teacher or another trusted adult.</P>
      <P>You will not be punished for raising a concern in good faith.</P>
      <P>If you are in immediate danger, seek help from a trusted adult and contact emergency services.</P>
    </div>
  </div>
</Section>

{/* ── 15 ── */}
<Section num={15} title="Handling Complaints and Incidents">
  <P>Participating schools must maintain appropriate procedures for reviewing and responding to student safety concerns.</P>
  <P>Depending on the circumstances, action may include:</P>
  <Bullets items={[
    'Ensuring the student\u2019s immediate safety.',
    'Suspending or cancelling an experience.',
    'Restricting mentor participation.',
    'Informing parents or guardians where appropriate.',
    'Preserving relevant information.',
    'Investigating the reported concern.',
    'Coordinating with Giggly Gains regarding platform access.',
    'Reporting matters to competent authorities where legally required.',
  ]} />
  <div className="mt-3 space-y-3">
    <P>Giggly Gains may suspend or restrict platform access where credible platform-related safety concerns arise.</P>
    <P>Neither school-level review nor platform action replaces mandatory reporting to competent authorities where required by law.</P>
  </div>
</Section>

{/* ── 16 ── */}
<Section num={16} title="Experience Cancellation or Withdrawal">
  <P>Parents may withdraw their child's participation from an experience.</P>
  <P>Schools may cancel or suspend experiences for safety, administrative or other appropriate reasons.</P>
  <P>Mentors and host organizations must promptly communicate circumstances that could affect an experience's suitability or safety.</P>
  <P>Where a credible safety concern arises, participation should not continue until appropriate protective measures have been considered.</P>
  <P>A student's decision to report a concern or withdraw from an unsafe situation must not result in retaliation.</P>
</Section>

{/* ── 17 ── */}
<Section num={17} title="Accessibility and Individual Needs">
  <P>Participating schools and experience providers should consider reasonable accessibility and participation needs.</P>
  <P>These may relate to:</P>
  <Bullets items={[
    'Physical accessibility.',
    'Communication requirements.',
    'Learning needs.',
    'Age and maturity.',
    'Appropriate supervision.',
    'Other relevant participation arrangements.',
  ]} />
  <div className="mt-3 space-y-3">
    <P>Information about a student's individual needs must be handled sensitively and shared only where necessary and appropriately authorized.</P>
    <P>Schools should coordinate suitable arrangements with parents and experience providers.</P>
  </div>
</Section>

{/* ── 18 ── */}
<Section num={18} title="Responsibilities of Host Organizations">
  <P>Organizations hosting students must:</P>
  <Bullets items={[
    'Provide an appropriate and safe environment.',
    'Identify a responsible adult supervisor.',
    'Explain relevant workplace safety rules.',
    'Restrict access to hazardous or inappropriate areas.',
    'Ensure activities are suitable for students\u2019 ages.',
    'Maintain professional conduct.',
    'Protect student privacy.',
    'Report incidents promptly.',
    'Comply with applicable legal requirements.',
  ]} />
  <div className="mt-3"><P>Host organizations must not use participation in CareersPOV for unauthorized marketing, solicitation, financial exploitation or other unrelated purposes.</P></div>
</Section>

{/* ── 19 ── */}
<Section num={19} title="Role of CareersPOV and Giggly Gains">
  <P>CareersPOV provides technology infrastructure that helps participating schools organize career exploration and experiential learning activities.</P>
  <P>Giggly Gains operates the platform and is responsible for taking reasonable measures within its control to address platform-related safety risks.</P>
  <P>Participating schools are responsible for school administration, mentor suitability decisions, supervision and school-organized experiences.</P>
  <P>Mentors and host organizations are responsible for their conduct and the activities they provide.</P>
  <P>Giggly Gains does not automatically conduct independent criminal background checks, supervise every experience or inspect every host workplace.</P>
  <P>Where Giggly Gains receives a credible report of serious misconduct or a safeguarding concern, it may restrict relevant accounts, preserve appropriate records, cooperate with participating schools and take other necessary action.</P>
  <P>Giggly Gains will report matters to competent authorities where legally required.</P>
  <P>Nothing in these Guidelines excludes or limits responsibility or liability that cannot lawfully be excluded.</P>
</Section>

{/* ── 20 ── */}
<Section num={20} title="Compliance With Applicable Laws">
  <P>All participants must comply with applicable Indian laws and regulations, including those relating to:</P>
  <Bullets items={[
    'Child protection and safeguarding.',
    'The Protection of Children from Sexual Offences Act, 2012.',
    'Personal-data protection and privacy.',
    'Workplace safety.',
    'Child and adolescent labour.',
    'Education and school administration.',
    'Mandatory reporting of relevant offences.',
  ]} />
  <div className="mt-3 space-y-3">
    <P>Where applicable law requires reporting to competent authorities, internal reporting to a school or CareersPOV is not a substitute.</P>
    <P>Participating schools and host organizations remain responsible for complying with laws applicable to their activities.</P>
  </div>
</Section>

{/* ── 21 ── */}
<Section num={21} title="Changes to These Guidelines">
  <P>Giggly Gains may update these Guidelines to reflect changes in platform functionality, safeguarding practices, applicable laws or operational requirements.</P>
  <P>Updated versions will be published on CareersPOV.</P>
  <P>Where required, significant changes will be communicated to participating schools or users.</P>
</Section>

{/* ── 22 ── */}
<Section num={22} title="Contact Information">
  <Sub title="Student Safety and Experience Concerns" />
  <P>For concerns involving student participation, mentors, online sessions, workplace visits or other school-organized experiences, parents and students should contact their school's designated CareersPOV coordinator.</P>
  <P>Serious safeguarding matters should be escalated to the school's designated safeguarding authority.</P>
  <P>Where the coordinator or another school representative is involved in the concern, the matter may be reported directly to the principal, designated safeguarding authority or competent authorities.</P>

  <Sub title="Platform Security and Technical Concerns" />
  <div className="mt-3 bg-gray-50 rounded-xl p-5 sm:p-6 text-sm sm:text-[15px] text-[#4B5563] leading-[1.8]">
    <p className="font-semibold text-[#1F2937]">Giggly Gains</p>
    <p>Operator of CareersPOV</p>
    <p>Indian Partnership Firm</p>
    <p className="mt-3 font-semibold text-[#1F2937]">Business Address:</p>
    <p>R103, Ganga Platino</p>
    <p>Near EON IT Park, Kharadi</p>
    <p>Pune, Maharashtra 411014</p>
    <p>India</p>
    <p className="mt-3">Email: <a href="mailto:contact@gigglygains.com" className="text-[#F97D6A] hover:underline">contact@gigglygains.com</a></p>
  </div>
  <div className="mt-3"><P>For immediate danger or emergencies, contact appropriate emergency services or competent authorities without waiting for a response from the school or platform.</P></div>
</Section>

        </div>

        <div className="mt-12 pt-6 border-t border-gray-200 text-center">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="text-sm text-[#9CA3AF] hover:text-[#6B7280] transition-colors"
          >
            Back to top
          </button>
        </div>
      </div>
    </div>
  );
}
